import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { deviceService } from '@/services/device.service'
import { accountService } from '@/services/account.service'
import { ROUTES } from '@/config/routes'
import type { Account } from '@/types/account.types'
import type { DeviceType } from '@/types/device.types'

const deviceCreateSchema = z.object({
  name: z.string().min(1, 'Device name is required'),
  serialNumber: z.string().min(1, 'Serial number is required'),
  deviceType: z.enum(['watersense', 'rk900', 'dht11soil'] as const),
  accountId: z.string().min(1, 'Account is required'),
  modemId: z.string().optional(),
  bluetoothAddress: z.string().optional(),
  hardwareVersion: z.string().optional(),
  firmwareVersion: z.string().optional(),
})

type DeviceCreateFormData = z.infer<typeof deviceCreateSchema>

const DEVICE_TYPES: { value: DeviceType; label: string }[] = [
  { value: 'watersense', label: 'WaterSense' },
  { value: 'rk900', label: 'RK900' },
  { value: 'dht11soil', label: 'DHT11 Soil' },
]

export const DeviceNewPage = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm<DeviceCreateFormData>({
    resolver: zodResolver(deviceCreateSchema)
  })

  const selectedAccountId = watch('accountId')

  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await accountService.getAccounts()
        const accountsList = response.accounts || []
        setAccounts(accountsList)

        // Set default account to Remuda USA Warehouse if available
        const defaultAccountId = 'b668b3cc-800e-459f-b963-755a7d4805a7'
        const defaultAccount = accountsList.find(acc => acc.id === defaultAccountId)
        if (defaultAccount) {
          setValue('accountId', defaultAccountId)
        }
      } catch (err: any) {
        setError('Failed to load accounts')
        console.error('Failed to fetch accounts:', err)
      } finally {
        setIsLoadingAccounts(false)
      }
    }
    fetchAccounts()
  }, [setValue])

  const onSubmit = async (data: DeviceCreateFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      await deviceService.createDevice({
        name: data.name,
        serialNumber: data.serialNumber,
        deviceType: data.deviceType,
        accountId: data.accountId,
        modemId: data.modemId,
        hardwareVersion: data.hardwareVersion,
        firmwareVersion: data.firmwareVersion,
      })

      navigate(ROUTES.DEVICES)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create device. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <CardTitle>Create New Device</CardTitle>
          <CardDescription>Add a new device to the system</CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-6">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Device Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Living Room Sensor"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="serialNumber">Serial Number *</Label>
                <Input
                  id="serialNumber"
                  type="text"
                  placeholder="SN-123456"
                  {...register('serialNumber')}
                />
                {errors.serialNumber && (
                  <p className="text-sm text-destructive">{errors.serialNumber.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="deviceType">Device Type *</Label>
                <Select onValueChange={(value) => setValue('deviceType', value as DeviceType)}>
                  <SelectTrigger id="deviceType">
                    <SelectValue placeholder="Select device type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEVICE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.deviceType && (
                  <p className="text-sm text-destructive">{errors.deviceType.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="accountId">Account *</Label>
                <Select
                  value={selectedAccountId}
                  onValueChange={(value) => setValue('accountId', value)}
                  disabled={isLoadingAccounts}
                >
                  <SelectTrigger id="accountId">
                    <SelectValue placeholder={isLoadingAccounts ? "Loading accounts..." : "Select account"} />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(account => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.accountId && (
                  <p className="text-sm text-destructive">{errors.accountId.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="modemId">Modem ID (Optional)</Label>
                <Input
                  id="modemId"
                  type="text"
                  placeholder="MODEM-001"
                  {...register('modemId')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bluetoothAddress">Bluetooth Address (Optional)</Label>
                <Input
                  id="bluetoothAddress"
                  type="text"
                  placeholder="00:11:22:33:44:55"
                  {...register('bluetoothAddress')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="hardwareVersion">Hardware Version (Optional)</Label>
                <Input
                  id="hardwareVersion"
                  type="text"
                  placeholder="v1.0.0"
                  {...register('hardwareVersion')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="firmwareVersion">Firmware Version (Optional)</Label>
                <Input
                  id="firmwareVersion"
                  type="text"
                  placeholder="v2.1.0"
                  {...register('firmwareVersion')}
                />
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(ROUTES.DEVICES)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating Device...' : 'Create Device'}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}